import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import concesionariaAreaDespachoService from '../../services/concesionariaAreaDespachoService';
import { getProjects } from '../../services/projectsService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';

const AreaDespacho = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogProjects, setCatalogProjects] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [modalRowId, setModalRowId] = useState(null);
  const [modalNombre, setModalNombre] = useState('');
  const [modalProjectId, setModalProjectId] = useState('');
  const [modalActive, setModalActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await concesionariaAreaDespachoService.getAreas();
      if (res.success) {
        setRows(res.data || []);
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al cargar áreas de despacho',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      try {
        const res = await getProjects({ active_only: 1 });
        if (!cancelled && res?.success && Array.isArray(res.data)) {
          setCatalogProjects(res.data);
        }
      } catch {
        if (!cancelled) {
          setToast({
            isOpen: true,
            message: 'No se pudo cargar el catálogo de proyectos y operaciones',
            type: 'error',
          });
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const proyectosOptions = useMemo(
    () =>
      [...catalogProjects]
        .filter((p) => p.type === 'proyecto')
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [catalogProjects]
  );

  const operacionesOptions = useMemo(
    () =>
      [...catalogProjects]
        .filter((p) => p.type === 'operacion')
        .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [catalogProjects]
  );

  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [rows]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setModalRowId(null);
    setModalNombre('');
    setModalProjectId('');
    setModalActive(true);
    setModalMode('create');
  }, []);

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalOpen, closeModal]);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const resolveRowProjectId = (row) => {
    if (row.project_id != null) return String(row.project_id);
    if (row.proyecto_id != null) return String(row.proyecto_id);
    if (row.operacion_id != null) return String(row.operacion_id);
    return '';
  };

  const openModalCreate = () => {
    setModalMode('create');
    setModalRowId(null);
    setModalNombre('');
    setModalProjectId('');
    setModalActive(true);
    setModalOpen(true);
  };

  const openModalEdit = (row) => {
    setModalMode('edit');
    setModalRowId(row.id);
    setModalNombre(row.name);
    setModalProjectId(resolveRowProjectId(row));
    setModalActive(!!row.is_active);
    setModalOpen(true);
  };

  const submitModal = async (e) => {
    e.preventDefault();
    const t = modalNombre.trim();
    if (!t) {
      setToast({ isOpen: true, message: 'El nombre no puede estar vacío', type: 'error' });
      return;
    }
    if (!modalProjectId) {
      setToast({ isOpen: true, message: 'Seleccione un proyecto u operación', type: 'error' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: t,
        is_active: modalActive,
        project_id: Number(modalProjectId),
      };
      if (modalMode === 'create') {
        const res = await concesionariaAreaDespachoService.createArea(payload);
        if (res.success) {
          setToast({ isOpen: true, message: 'Área creada', type: 'success' });
        }
      } else if (modalRowId) {
        const res = await concesionariaAreaDespachoService.updateArea(modalRowId, payload);
        if (res.success) {
          setToast({ isOpen: true, message: 'Cambios guardados', type: 'success' });
        }
      }
      await loadRows();
      closeModal();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors || 'No se pudo guardar';
      const str = typeof msg === 'object' ? Object.values(msg).flat().join(' ') : String(msg);
      setToast({ isOpen: true, message: str, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      const res = await concesionariaAreaDespachoService.deleteArea(deleteTarget.id);
      if (res.success) {
        setToast({ isOpen: true, message: 'Área eliminada', type: 'success' });
        setDeleteTarget(null);
        await loadRows();
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'Error al eliminar',
        type: 'error',
      });
    }
  };

  const catalogEmpty = !catalogLoading && proyectosOptions.length === 0 && operacionesOptions.length === 0;

  const modal = modalOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-label="Cerrar"
            onClick={closeModal}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-area-despacho-title"
            className="relative w-full max-w-lg rounded-xl bg-white shadow-xl border border-gray-200"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 id="modal-area-despacho-title" className="text-lg font-semibold text-gray-900">
                {modalMode === 'create' ? 'Nueva área de despacho' : 'Editar área de despacho'}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {modalMode === 'create'
                  ? 'Elija un proyecto o una operación del catálogo de configuración (solo uno).'
                  : 'Modifique nombre, proyecto u operación, o estado.'}
              </p>
            </div>
            <form onSubmit={submitModal} className="px-5 py-4 space-y-4">
              <div>
                <label htmlFor="modal-area-nombre" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  id="modal-area-nombre"
                  type="text"
                  value={modalNombre}
                  onChange={(e) => setModalNombre(e.target.value)}
                  placeholder="Ej. Línea caliente, expedición A…"
                  autoFocus
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
              <div>
                <label htmlFor="modal-area-proyecto-operacion" className="block text-sm font-medium text-gray-700 mb-1">
                  Proyecto u operación <span className="text-red-500">*</span>
                </label>
                <select
                  id="modal-area-proyecto-operacion"
                  value={modalProjectId}
                  onChange={(e) => setModalProjectId(e.target.value)}
                  disabled={catalogLoading}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:bg-gray-100"
                >
                  <option value="">
                    {catalogLoading ? 'Cargando catálogo…' : 'Seleccione proyecto u operación'}
                  </option>
                  {proyectosOptions.length > 0 && (
                    <optgroup label="Proyectos">
                      {proyectosOptions.map((p) => (
                        <option key={`p-${p.id}`} value={String(p.id)}>
                          {p.code ? `${p.code} — ` : ''}
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {operacionesOptions.length > 0 && (
                    <optgroup label="Operaciones">
                      {operacionesOptions.map((p) => (
                        <option key={`o-${p.id}`} value={String(p.id)}>
                          {p.code ? `${p.code} — ` : ''}
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                {catalogEmpty && (
                  <p className="mt-1 text-xs text-amber-700">
                    No hay proyectos ni operaciones activos. Cree registros en Configuración → Proyectos (tipo
                    proyecto u operación).
                  </p>
                )}
              </div>
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Estado</span>
                <div className="flex flex-wrap gap-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modal-area-estado"
                      checked={modalActive === true}
                      onChange={() => setModalActive(true)}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-800">Activo</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="modal-area-estado"
                      checked={modalActive === false}
                      onChange={() => setModalActive(false)}
                      className="text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-800">Inactivo</span>
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!modalNombre.trim() || !modalProjectId || saving || catalogLoading}
                  className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium"
                >
                  {saving ? 'Guardando…' : modalMode === 'create' ? 'Agregar' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  const displayCatalog = (row) => {
    const cp = row.catalog_project || row.proyecto || row.operacion;
    if (!cp) return <span className="text-gray-400">—</span>;
    const tipo = cp.type === 'operacion' ? 'Operación' : 'Proyecto';
    return (
      <div className="text-gray-800">
        <span className="inline-block mr-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">
          {tipo}
        </span>
        <span className="text-gray-500 font-mono text-xs mr-1">{cp.code}</span>
        {cp.name}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl mx-auto flex justify-center items-center min-h-[12rem]">
        <p className="text-gray-600">Cargando áreas de despacho…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {modal}

      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">
          Dashboard
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">
          Producción concesionaria
        </Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria/configuraciones" className="hover:text-teal-600">
          Configuración
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Área de despacho</span>
      </nav>

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Área de despacho</h1>

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-x-auto">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm font-medium text-gray-700">Catálogo de áreas de despacho</span>
          <button
            type="button"
            onClick={openModalCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors"
          >
            + Agregar área
          </button>
        </div>
        {sorted.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            No hay áreas registradas. Pulse <strong className="text-gray-700">Agregar área</strong> para crear una.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3 font-medium w-16">Orden</th>
                <th className="text-left px-4 py-3 font-medium">Nombre</th>
                <th className="text-left px-4 py-3 font-medium min-w-[14rem]">Proyecto u operación</th>
                <th className="text-left px-4 py-3 font-medium">Estado</th>
                <th className="text-right px-4 py-3 font-medium w-36">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sorted.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 align-middle text-gray-500 tabular-nums">{row.sort_order}</td>
                  <td className="px-4 py-2 align-middle">
                    <span className="font-medium text-gray-900">{row.name}</span>
                  </td>
                  <td className="px-4 py-2 align-middle">{displayCatalog(row)}</td>
                  <td className="px-4 py-2 align-middle">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {row.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 align-middle text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => openModalEdit(row)}
                      className="text-sm text-teal-700 hover:text-teal-900 font-medium mr-3"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(row)}
                      className="text-sm text-red-600 hover:text-red-800 font-medium"
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-4">
        <Link
          to="/produccion-concesionaria/configuraciones/tipo-plato"
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          ← Tipo de plato
        </Link>
        <Link
          to="/produccion-concesionaria/configuraciones"
          className="text-teal-700 text-sm font-medium hover:underline"
        >
          ← Configuración
        </Link>
      </div>

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar área de despacho"
        message={deleteTarget ? `¿Eliminar «${deleteTarget.name}»?` : ''}
        confirmText="Eliminar"
        cancelText="Cancelar"
        type="danger"
      />
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, isOpen: false }))}
      />
    </div>
  );
};

export default AreaDespacho;
